import { type VariantProps } from 'class-variance-authority';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  MoreHorizontal,
} from 'lucide-react';
import { forwardRef, HTMLAttributes } from 'react';
import { paginationVariants } from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import { Button } from './button';
import { Select, type SelectOption } from './select';

/**
 * Pagination component props
 */
export interface PaginationProps
  extends HTMLAttributes<HTMLElement>, VariantProps<typeof paginationVariants> {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly pageSize: number;
  readonly totalItems: number;
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange?: ((pageSize: number) => void) | undefined;
  readonly pageSizeOptions?: readonly number[] | undefined;
  readonly showPageSize?: boolean | undefined;
  readonly showFirstLast?: boolean | undefined;
  readonly showResults?: boolean | undefined;
  readonly maxVisiblePages?: number | undefined;
  readonly disabled?: boolean | undefined;
}

/**
 * Generate page numbers with ellipsis for pagination
 */
function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  maxVisible: number
): (number | 'ellipsis')[] {
  if (totalPages <= maxVisible) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];
  const halfVisible = Math.floor(maxVisible / 2);

  // Always show first page
  pages.push(1);

  // Calculate start and end of visible range
  let start = Math.max(2, currentPage - halfVisible);
  let end = Math.min(totalPages - 1, currentPage + halfVisible);

  // Adjust if we're near the start
  if (currentPage <= halfVisible + 1) {
    end = Math.min(maxVisible - 1, totalPages - 1);
  }

  // Adjust if we're near the end
  if (currentPage >= totalPages - halfVisible) {
    start = Math.max(2, totalPages - maxVisible + 2);
  }

  // Add ellipsis after first page if needed
  if (start > 2) {
    pages.push('ellipsis');
  }

  // Add visible page numbers
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Add ellipsis before last page if needed
  if (end < totalPages - 1) {
    pages.push('ellipsis');
  }

  // Always show last page if there's more than one page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
}

/**
 * Pagination component with responsive design
 * Automatically adapts on mobile devices
 *
 * Features:
 * - Page numbers with ellipsis
 * - First/Last page navigation
 * - Previous/Next navigation
 * - Page size selector
 * - Results summary
 * - Mobile-responsive (fewer controls on small screens)
 * - WCAG 2.1 AA compliant
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={2}
 *   totalPages={10}
 *   pageSize={10}
 *   totalItems={95}
 *   onPageChange={setPage}
 *   onPageSizeChange={setPageSize}
 * />
 * ```
 */
export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      className,
      currentPage,
      totalPages,
      pageSize,
      totalItems,
      onPageChange,
      onPageSizeChange,
      pageSizeOptions = [10, 25, 50, 100],
      showPageSize = true,
      showFirstLast = true,
      showResults = true,
      maxVisiblePages = 7,
      disabled = false,
      size,
      alignment,
      ...props
    },
    ref
  ) => {
    const hasNextPage = currentPage < totalPages;
    const hasPreviousPage = currentPage > 1;

    const startIndex = (currentPage - 1) * pageSize + 1;
    const endIndex = Math.min(currentPage * pageSize, totalItems);

    const pages = generatePageNumbers(currentPage, totalPages, maxVisiblePages);

    // Page size options for Select
    const pageSizeSelectOptions: readonly SelectOption[] = pageSizeOptions.map(
      (size) => ({
        value: size.toString(),
        label: `${size} per page`,
      })
    );

    return (
      <nav
        ref={ref}
        role="navigation"
        aria-label="Pagination"
        className={cn(paginationVariants({ size, alignment }), className)}
        {...props}
      >
        {/* Results summary */}
        {showResults && (
          <div
            className="text-sm text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            Showing{' '}
            <span className="font-medium text-foreground">{startIndex}</span> to{' '}
            <span className="font-medium text-foreground">{endIndex}</span> of{' '}
            <span className="font-medium text-foreground">{totalItems}</span>{' '}
            results
          </div>
        )}

        {/* Pagination controls */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Page size selector */}
          {showPageSize && onPageSizeChange && (
            <div className="flex items-center gap-2">
              <Select
                value={pageSize.toString()}
                onChange={(value) => {
                  if (value) {
                    onPageSizeChange(Number(value));
                  }
                }}
                options={pageSizeSelectOptions}
                disabled={disabled}
                className="w-full sm:w-40"
                aria-label="Select page size"
              />
            </div>
          )}

          {/* Page navigation */}
          <div className="flex items-center gap-1">
            {/* First page button */}
            {showFirstLast && (
              <Button
                type="button"
                variant={ButtonVariant.Outline}
                size={ButtonSize.Icon}
                onClick={() => onPageChange(1)}
                disabled={!hasPreviousPage || disabled}
                className="hidden size-11 min-h-[44px] min-w-[44px] sm:inline-flex"
                aria-label="Go to first page"
              >
                <ChevronsLeft className="size-4" aria-hidden="true" />
              </Button>
            )}

            {/* Previous page button */}
            <Button
              type="button"
              variant={ButtonVariant.Outline}
              size={ButtonSize.Icon}
              onClick={() => onPageChange(currentPage - 1)}
              disabled={!hasPreviousPage || disabled}
              className="size-11 min-h-[44px] min-w-[44px]"
              aria-label="Go to previous page"
            >
              <ChevronLeft className="size-4" aria-hidden="true" />
            </Button>

            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {pages.map((page, index) => {
                if (page === 'ellipsis') {
                  return (
                    <div
                      key={`ellipsis-${index}-${currentPage}`}
                      className="flex size-11 items-center justify-center"
                      aria-hidden="true"
                    >
                      <MoreHorizontal className="size-4 text-muted-foreground" />
                    </div>
                  );
                }

                const isCurrentPage = page === currentPage;

                return (
                  <Button
                    key={page}
                    type="button"
                    variant={
                      isCurrentPage
                        ? ButtonVariant.Default
                        : ButtonVariant.Outline
                    }
                    size={ButtonSize.Icon}
                    onClick={() => onPageChange(page)}
                    disabled={disabled}
                    className={cn(
                      'h-11 w-11 min-h-[44px] min-w-[44px]',
                      'hidden sm:inline-flex',
                      isCurrentPage && 'pointer-events-none'
                    )}
                    aria-label={`Go to page ${page}`}
                    aria-current={isCurrentPage ? 'page' : undefined}
                  >
                    {page}
                  </Button>
                );
              })}

              {/* Mobile: Current page indicator */}
              <div
                className="flex h-11 min-h-[44px] items-center justify-center px-3 text-sm font-medium text-foreground sm:hidden"
                aria-live="polite"
                aria-atomic="true"
              >
                Page {currentPage} of {totalPages}
              </div>
            </div>

            {/* Next page button */}
            <Button
              type="button"
              variant={ButtonVariant.Outline}
              size={ButtonSize.Icon}
              onClick={() => onPageChange(currentPage + 1)}
              disabled={!hasNextPage || disabled}
              className="size-11 min-h-[44px] min-w-[44px]"
              aria-label="Go to next page"
            >
              <ChevronRight className="size-4" aria-hidden="true" />
            </Button>

            {/* Last page button */}
            {showFirstLast && (
              <Button
                type="button"
                variant={ButtonVariant.Outline}
                size={ButtonSize.Icon}
                onClick={() => onPageChange(totalPages)}
                disabled={!hasNextPage || disabled}
                className="hidden size-11 min-h-[44px] min-w-[44px] sm:inline-flex"
                aria-label="Go to last page"
              >
                <ChevronsRight className="size-4" aria-hidden="true" />
              </Button>
            )}
          </div>
        </div>
      </nav>
    );
  }
);

Pagination.displayName = 'Pagination';

export {
  PaginationSize,
  PaginationAlignment,
} from '@/shared/types/pagination.types';
